import { Icon } from "@iconify/react";
import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AppointmentForm.css";
import { BASE_URL } from "../../helpers/api";
import { useNavigate } from "react-router-dom";
import LoginPopup from "../LoginPopup/LoginPopup";

export default function AppointmentForm() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName]   = useState("");
  const [phone, setPhone] = useState("");;
  const [reason, setReason] = useState("");
  const [timeSlot, setTimeSlot] = useState("");
  const [slotIndex, setSlotIndex] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [location, setLocation] = useState("");
  const [speciality, setSpeciality] = useState("");
  const [doctorList, setDoctorList] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [timeSlots, setTimeSlots] = useState({ slots: [], scheduleId: null });
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [locationsData, setLocationsData] = useState([]);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState(false);



  // Dynamic timezone map built from API response
const timezoneMap = React.useMemo(() => {
  const map = {};
  locationsData.forEach(loc => {
    map[String(loc.location_id)] = loc.timezone_label || "UTC";
  });
  return map;
}, [locationsData]);

  // Auth check
  useEffect(() => {
    // ✅ Fix 1 — Auth check in useEffect
    const userId = localStorage.getItem("userId") || sessionStorage.getItem("userId");
    const token  = localStorage.getItem("token")  || sessionStorage.getItem("token");
    if (!userId || !token) {
      setShowLoginPopup(true);
      return;
    }

    const storedDoctor = localStorage.getItem("selectedDoctor");
    if (storedDoctor) {
      const doctorObj = JSON.parse(storedDoctor);
      setSelectedDoctor(doctorObj.id);
    }
  }, []);


  // ── Define OUTSIDE useEffect so it's accessible everywhere ──
  const fetchProfile = async () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (!token) return;
  try {
    const res  = await fetch(`${BASE_URL}/user/profile?token=${token}`);
    const data = await res.json();
    if (data && !data.error) {
      const fullName = [data.firstname, data.lastname]
        .filter(Boolean)
        .join(" ")
        .trim();
      setName(fullName);
      setEmail(data.email  || "");
      setPhone(data.mobile || "");

     // ✅ CORRECT
    const storage = localStorage.getItem("token") ? localStorage : sessionStorage;
    storage.setItem("userName",  fullName);
    storage.setItem("userEmail", data.email  || "");
    storage.setItem("userPhone", data.mobile || "");
    }
  } catch (err) {
    console.error("Error fetching profile:", err);
  }
  };

    // ── Call on page load ──
    useEffect(() => {
      fetchProfile();
    }, []);

  // Fetch locations data on page load
  useEffect(() => {
    const fetchLocationsData = async () => {
      try {
        const res  = await fetch(`${BASE_URL}/location-specialties-doctors`);
        const data = await res.json();
        if (Array.isArray(data)) {
          setLocationsData(data);

          const savedLocation   = localStorage.getItem("selectedLocation");
          const savedSpeciality = localStorage.getItem("selectedSpeciality");
          const savedDate       = localStorage.getItem("selectedDate");
          const savedDoctorId   = localStorage.getItem("selectedDoctorId");

          if (savedLocation)  setLocation(savedLocation);
          if (savedDate)      setSelectedDate(new Date(savedDate));

          if (savedLocation && savedSpeciality) {
            const locData = data.find(
              (loc) => String(loc.location_id) === String(savedLocation)
            );
            if (
              locData &&
              locData.specialties.some(
                (s) => String(s.id) === String(savedSpeciality)
              )
            ) {
              setSpeciality(savedSpeciality);
            }
          }

          if (savedDoctorId) setSelectedDoctor(Number(savedDoctorId));
        }
      } catch (err) {
        console.error("Error fetching locations with doctors:", err);
      }
    };
    fetchLocationsData();
  }, []);

  // Update doctor list when location/speciality changes
  // ✅ FIX: Always reset selectedDoctor when location/speciality changes
  //         so slots are always fetched fresh for the new doctor
  useEffect(() => {
    if (!location || !speciality || !locationsData.length) {
      setDoctorList([]);
      setSelectedDoctor(null);
      setTimeSlot("");
      setSlotIndex(0); // ← add this
      setTimeSlots({ slots: [], scheduleId: null });
      return;
    }

    const locData = locationsData.find(
      (loc) => String(loc.location_id) === String(location)
    );
    if (!locData) { setDoctorList([]); return; }

    const specData = locData.specialties.find(
      (spec) => String(spec.id) === String(speciality)
    );
    if (!specData) { setDoctorList([]); return; }

    const doctors = (specData.doctors || []).map((doc) => ({
      doctor_id:   doc.doctor_id,
      doctor_name: doc.doctor_name,
    }));

    setDoctorList(doctors);

    // ✅ Always reset to first doctor in new list
    //    Don't keep old doctor — forces fresh slot fetch
    if (doctors.length > 0) {
      setSelectedDoctor(doctors[0].doctor_id);
    } else {
      setSelectedDoctor(null);
    }

    // Reset slots whenever doctor list changes
    setTimeSlot("");
    setSlotIndex(0); // ← add this
    setTimeSlots({ slots: [], scheduleId: null });

  }, [speciality, location, locationsData]);

  // ✅ FIX: Handle manual doctor change — always reset slots and force re-fetch
  const handleDoctorChange = (e) => {
    const doctorId = parseInt(e.target.value);
    setTimeSlot("");
    setSlotIndex(0); // ← add this
    setTimeSlots({ slots: [], scheduleId: null });
    setSelectedDoctor(doctorId); // set AFTER reset so useEffect triggers with new value
    localStorage.setItem("selectedDoctorId", doctorId);
  };

  // Fetch available time slots
  // ✅ FIX: Runs whenever selectedDoctor, selectedDate, or location changes
  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedDoctor || !selectedDate || !location) {
        setTimeSlots({ slots: [], scheduleId: null });
        return;
      }

      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;

      try {
        const response = await fetch(
          `${BASE_URL}/doctors/${selectedDoctor}/availability`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ date: dateStr, location_id: location }),
          }
        );

        const data = await response.json();

        if (Array.isArray(data) && data.length > 0) {
          const matchedDay = data.find((item) => item.date === dateStr);
          setTimeSlots(
            matchedDay?.time_slots
              ? { slots: matchedDay.time_slots, scheduleId: matchedDay.schedule_id }
              : { slots: [], scheduleId: null }
          );
        } else {
          setTimeSlots({ slots: [], scheduleId: null });
        }
      } catch (err) {
        console.error("Error fetching slots:", err);
        setTimeSlots({ slots: [], scheduleId: null });
      }
    };

    fetchSlots();
  }, [selectedDoctor, selectedDate, location]);

  // Form validation
  const validateEmail = (value) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!value) return "Email is required";
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return "";
  };

  const validateForm = () => {
    const errors = {};
    // name, phone, email are readonly — no validation needed
    if (!location)       errors.location   = "Location is required";
    if (!speciality)     errors.speciality = "Speciality is required";
    if (!selectedDoctor) errors.doctor     = "Doctor is required";
    if (!selectedDate)   errors.date       = "Date is required";
    if (!timeSlot)       errors.timeSlot   = "Time slot is required";
    if (!reason)         errors.reason     = "Reason for visit is required";
    return errors;
  };

  // const serialNo = Array.isArray(timeSlots.slots)
  //   ? timeSlots.slots.indexOf(timeSlot) + 1
  //   : null;

  const serialNo = slotIndex;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload = {
      name,
      email,
      mobile: phone,
      location,
      token: localStorage.getItem("token") || sessionStorage.getItem("token") || null,
      specialty_id: speciality,
      doctor_id:    selectedDoctor,
      date: selectedDate
        ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
        : null,
      time_slot:   timeSlot,
      problem:     reason,
      serial_no:   serialNo,
      schedule_id: timeSlots.scheduleId,
    };

    try {
      const res  = await fetch(`${BASE_URL}/appointments/submit`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      console.log("DEBUG RESPONSE:", data);

      // ── Check if the request actually succeeded ──
      const isSuccess = res.ok && data.appointment_id;

      if (isSuccess) {
        // ✅ SUCCESS
        localStorage.setItem("appointment_id", data.appointment_id);
        setModalMessage(data.message || "Appointment successfully created!");
        setIsSuccess(true);
        setModalVisible(true);

        // Reset form only on success
        setReason("");
        setTimeSlot(""); setSlotIndex(0); setSpeciality(""); setLocation("");
        setSelectedDoctor(null); setDoctorList([]);
        setSelectedDate(null);
        setTimeSlots({ slots: [], scheduleId: null });
        setSubmitted(false); setFormErrors({});

        fetchProfile();

        localStorage.removeItem("selectedSpeciality");
        localStorage.removeItem("selectedLocation");
        localStorage.removeItem("selectedDate");
        localStorage.removeItem("selectedDoctorId");
      } else {
        // ❌ API ERROR (slot booked, invalid session, etc.)
        const errorMsg = data.error 
                      || data.message 
                      || "Something went wrong. Please try again.";
        setModalMessage(errorMsg);
        setIsSuccess(false);
        setModalVisible(true);
      }
    } catch (err) {
      // ❌ NETWORK ERROR (no internet, server down, CORS, etc.)
      console.error("Error submitting appointment:", err);
      setModalMessage("Unable to connect to the server. Please check your internet and try again.");
      setIsSuccess(false);
      setModalVisible(true);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="row">

        {/* Name */}
        <div className="col-lg-6">
          <label className="cs_input_label cs_heading_color">Name <span style={{color: "red"}}>*</span></label>
          <input
              type="text"
              className="cs_form_field"
              value={name}
              readOnly
              style={{ cursor: "not-allowed" }}
          />
          {submitted && formErrors.name && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.name}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Phone */}
        <div className="col-lg-6">
          <label className="cs_input_label cs_heading_color">Phone Number <span style={{color: "red"}}>*</span></label>
          <input
              type="text"
              className="cs_form_field"
              value={phone}
              readOnly
              style={{ cursor: "not-allowed" }}
          />
          {submitted && formErrors.phone && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.phone}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Email */}
        <div className="col-lg-12">
          <label className="cs_input_label cs_heading_color">Mail ID <span style={{color: "red"}}>*</span></label>
          <input
            type="text"
            className="cs_form_field"
            value={email}
            readOnly
            style={{ cursor: "not-allowed" }}
          />
          {submitted && formErrors.email && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.email}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Location */}
        <div className="col-lg-12">
          <label className="cs_input_label cs_heading_color">Location <span style={{color: "red"}}>*</span></label>
          <select
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              localStorage.setItem("selectedLocation", e.target.value);
              setSpeciality("");
              setSelectedDoctor(null);
              setDoctorList([]);
              setTimeSlot("");
              setSlotIndex(0); // ← add this
              setTimeSlots({ slots: [], scheduleId: null });
            }}
            className="cs_form_field"
          >
            <option value="">Select Location</option>
            {locationsData.map((loc) => (
              <option key={loc.location_id} value={loc.location_id}>
                {loc.location_name}
              </option>
            ))}
          </select>
          {submitted && formErrors.location && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.location}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Specialization */}
        <div className="col-lg-12">
          <label className="cs_input_label cs_heading_color">Specialization <span style={{color: "red"}}>*</span></label>
          <select
            value={speciality}
            onChange={(e) => {
              setSpeciality(e.target.value);
              localStorage.setItem("selectedSpeciality", e.target.value);
              setSelectedDoctor(null);
              setTimeSlot("");
              setSlotIndex(0); // ← add this
              setTimeSlots({ slots: [], scheduleId: null });
            }}
            className="cs_form_field"
            disabled={!location}
          >
            <option value="">Select Speciality</option>
            {locationsData
              .find((loc) => String(loc.location_id) === String(location))
              ?.specialties.map((spec) => (
                <option key={spec.id} value={spec.id}>
                  {spec.name}
                </option>
              ))}
          </select>
          {submitted && formErrors.speciality && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.speciality}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Doctor */}
        <div className="col-lg-12">
          <label className="cs_input_label cs_heading_color">Doctor Name <span style={{color: "red"}}>*</span></label>
          <select
            className="cs_form_field"
            value={selectedDoctor || ""}
            onChange={handleDoctorChange}
          >
            <option value="">Select Doctor</option>
            {doctorList.map((doc) => (
              <option key={doc.doctor_id} value={doc.doctor_id}>
                {doc.doctor_name}
              </option>
            ))}
          </select>
          {submitted && formErrors.doctor && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.doctor}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Date */}
        <div className="col-lg-6">
          <label className="cs_input_label cs_heading_color">Preferred Date <span style={{color: "red"}}>*</span></label>
          <div className="cs_with_icon_input relative">
            <DatePicker
              selected={selectedDate}
              onChange={(date) => {
                setSelectedDate(date);
                setTimeSlot("");
                setSlotIndex(0); // ← add this
                setTimeSlots({ slots: [], scheduleId: null });
                if (date)
                  localStorage.setItem("selectedDate", date.toISOString().split("T")[0]);
              }}
              dateFormat="dd/MM/yyyy"
              minDate={new Date()}
              isClearable
              placeholderText="dd/mm/yyyy"
              className="cs_form_field w-full"
            />
            <i className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <Icon icon="fa6-solid:calendar-days" />
            </i>
          </div>
          {submitted && formErrors.date && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.date}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Time Slots */}
        <div className="col-lg-6">
          <label className="cs_input_label cs_heading_color">Preferred Time <span style={{color: "red"}}>*</span></label>
          <div className="cs_with_icon_input relative">
            <select
              className="cs_form_field w-full"
              value={timeSlot}
              onChange={(e) => {
                  setTimeSlot(e.target.value);
                  const selectedOption = e.target.options[e.target.selectedIndex];
                  setSlotIndex(parseInt(selectedOption.getAttribute('data-serial')));
              }}
            >
              <option value="">Select Time</option>
              {Array.isArray(timeSlots.slots) && timeSlots.slots.length > 0 ? (
                timeSlots.slots.map((slot, idx) => {
                const timezone = timezoneMap[String(location)] || "UTC";
                const [hourStr, minuteStr] = slot.time.split(":");
                const hour        = parseInt(hourStr);
                const period      = hour >= 12 ? "PM" : "AM";
                const displayHour = hour % 12 || 12;
                const displayTime = `${displayHour}:${minuteStr} ${period}`;
                return (
                    <option key={idx} value={displayTime} data-serial={slot.serial_no}>
                        {displayTime} ({timezone})
                    </option>
                );
            })
                ) : (
                <option disabled>No time slots available</option>
              )}
            </select>
            <i className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <Icon icon="fa6-regular:clock" />
            </i>
          </div>
          {submitted && formErrors.timeSlot && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.timeSlot}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Reason */}
        <div className="col-lg-12">
          <label className="cs_input_label cs_heading_color">Reason for Visit <span style={{color: "red"}}>*</span></label>
          <div className="cs_radio_group">
            {["Routine Checkup", "New Patient Visit", "Specific Concern"].map((r) => (
              <div className="cs_radio_wrap" key={r}>
                <input
                  className="cs_radio_input"
                  type="radio"
                  name="reasonForVisit"
                  id={r}
                  value={r}
                  checked={reason === r}
                  onChange={(e) => setReason(e.target.value)}
                />
                <label className="cs_radio_label" htmlFor={r}>{r}</label>
              </div>
            ))}
          </div>
          {submitted && formErrors.reason && (
            <div style={{ color: "red", fontSize: "0.875rem" }}>{formErrors.reason}</div>
          )}
          <div className="cs_height_42 cs_height_xl_25" />
        </div>

        {/* Submit */}
        <div className="col-lg-12">
          <button type="submit" className="cs_btn cs_style_1">
            <span>Submit</span>
            <i>
              <img src="/images/icons/arrow_white.svg" alt="Icon" />
              <img src="/images/icons/arrow_white.svg" alt="Icon" />
            </i>
          </button>
        </div>

      </form>

      {/* Success / Error Modal */}
      {modalVisible && (
        <div className="modal-container">
          <button
            onClick={() => setModalVisible(false)}
            style={{
              position: "absolute", top: "10px", right: "10px",
              background: "transparent", border: "none",
              fontSize: "22px", cursor: "pointer", color: "gray", fontWeight: "normal",
            }}
          >
            ×
          </button>

          {/* Icon based on result */}
          {/* <div style={{ textAlign: "center", marginBottom: "10px" }}>
            {isSuccess ? (
              <Icon icon="fa6-solid:circle-check" style={{ fontSize: "48px", color: "#28a745" }} />
            ) : (
              <Icon icon="fa6-solid:circle-exclamation" style={{ fontSize: "48px", color: "#dc3545" }} />
            )}
          </div> */}

          {/* Heading */}
          <h3 style={{ textAlign: "center", marginBottom: "10px"}}>
            {isSuccess ? "Appointment Booked" : "Booking Failed"}
          </h3>

          {/* Message */}
          <p style={{ textAlign: "center" }}>{modalMessage}</p>

          {/* Action button — different for success vs error */}
          {isSuccess ? (
            <button
              className="cs_btn cs_style_1"
              onClick={() => {
                setModalVisible(false);
                window.location.href = "/payment";
              }}
            >
              <span>Proceed to Payment</span>
              <i>
                <img src="/images/icons/arrow_white.svg" alt="Icon" />
                <img src="/images/icons/arrow_white.svg" alt="Icon" />
              </i>
            </button>
          ) : (
            <button
              className="cs_btn cs_style_1"
              onClick={() => setModalVisible(false)}
            >
              <span>Try Again</span>
            </button>
          )}
        </div>
      )}

      {/* Login Popup */}
      <LoginPopup show={showLoginPopup} redirectPath="/appointments" />
    </>
  );
}